import type { IApplicationBase } from "@/data/model/ApplicationBase";
import type { IArticle } from "@/data/model/Article";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { ICustomerArticle, ICustomerOffer, ICustomerOptionGroupOption } from "@/data/model/Customer";
import { ConsumptionSite, ConsumptionSiteDisplayMode, SchedulingMode, ServiceType } from "@/data/model/Enums";
import type { IOrderApplication } from "@/data/model/OrderApplication";
import type { IPayApplication } from "@/data/model/PayApplication";
import type { IProfile } from "@/data/model/Profile";
import type { IService } from "@/data/model/Service";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { toMoney } from "@/shared/utils/format";

import { TastierClient } from "@/app/TastierClient";
import { trans } from "@/app/localization/Trans";

export class Helper {
    public static creditCardIsExpired(card: ICreditCard): boolean {
        if (card.expiryDate === undefined) return false;

        const now = new Date();
        if (card.expiryDate.getFullYear() > now.getFullYear()) return false;

        if (card.expiryDate.getFullYear() >= now.getFullYear() && card.expiryDate.getMonth() >= now.getMonth())
            return false;

        return true;
    }

    public static shortNameFromProfile(profile: IProfile): string {
        let sn = "";

        if (profile?.firstName !== undefined && profile.firstName.length > 0) sn += profile.firstName.substr(0, 1);

        if (profile?.lastName !== undefined && profile.lastName.length > 0) sn += profile.lastName.substr(0, 1);

        return sn;
    }

    public static hasCartChanged(oldOffer: ICustomerOffer, newOffer: ICustomerOffer): boolean {
        if (oldOffer === undefined && newOffer === undefined) return false;

        if (oldOffer === undefined && newOffer !== undefined) return true;

        if (oldOffer !== undefined && newOffer === undefined) return true;

        if (oldOffer.cart === undefined && newOffer.cart === undefined) return false;

        if (oldOffer.cart === undefined && newOffer.cart !== undefined) return true;

        if (oldOffer.cart !== undefined && newOffer.cart === undefined) return true;

        if (oldOffer.cart!.items.length !== newOffer.cart!.items.length) return true;

        if (oldOffer.cart!.total !== newOffer.cart!.total) return true;

        for (const i in oldOffer.cart!.items) {
            const itm = oldOffer.cart!.items[i];

            const e = newOffer.cart!.items.find((c) => c.articleId === itm.articleId && c.quantity === itm.quantity);
            if (e === undefined) return true;

            //compare options
            if (e.options.length !== itm.options.length) return true;

            for (const ai in itm.options) {
                const opt = e.options.find((c) => c.id === itm.options[ai].id);
                if (opt === undefined) return true;
            }
        }

        return false;
    }

    public static hasService(serviceType: ServiceType): boolean {
        return this.getAvailableServices().some((c) => c.serviceType == serviceType);
    }

    private static getCurrentService(): IService | undefined {
        return this.getAvailableServices().find((c) => c.id == TastierClient.context.selectedServiceId);
    }

    public static getCurrentOrderOrOfferService(): IService | undefined {
        const currentService = this.getCurrentService();
        if (currentService == null) {
            return undefined;
        }
        if (currentService.serviceType == ServiceType.Order || currentService.serviceType == ServiceType.Offer) {
            return currentService;
        }

        return undefined;
    }

    public static getCurrentPayService(): IService | undefined {
        const currentService = this.getCurrentService();
        if (currentService == null || currentService.serviceType != ServiceType.Pay) {
            return undefined;
        }

        return currentService;
    }

    public static getCurrentUseCase(): IApplicationBase | undefined {
        return this.getCurrentOrderServiceOrderApplication() ?? this.getPayUseCase();
    }

    public static getCurrentOrderServiceOrderApplication(): IOrderApplication | undefined {
        return this.getCurrentOrderOrOfferService()?.useCase;
    }

    public static isDeliveryActive(): boolean {
        const orderApplication = this.getCurrentOrderServiceOrderApplication();
        if (orderApplication == null) {
            return false;
        }

        return orderApplication.isDeliveryActive;
    }

    public static getPayUseCase(): IPayApplication | undefined {
        return this.getCurrentPayService()?.payUseCase;
    }

    public static getOrderOrOfferServiceById(orderServiceId: string): IService | undefined {
        return this.getAvailableServices().find(
            (c) =>
                c.id == orderServiceId && (c.serviceType === ServiceType.Order || c.serviceType === ServiceType.Offer)
        );
    }

    public static getUseCaseByOrderOrOfferServiceId(orderServiceId?: string): IOrderApplication | undefined {
        if (orderServiceId == null) {
            return undefined;
        }
        return this.getOrderOrOfferServiceById(orderServiceId)?.useCase;
    }

    public static getCurrentServiceDesignation(): string | undefined {
        const service = this.getCurrentService();
        if (service === undefined) {
            return undefined;
        }

        const name = Format.translate(service.name, clientLanguage());
        if (name !== undefined) {
            return name;
        }

        return trans.enums.serviceType[ServiceType[service.serviceType]];
    }

    public static formatPrice(item: IArticle | ICustomerArticle | ICustomerOptionGroupOption): string {
        const price = this.sitePrice(item);
        return toMoney(price);
    }

    public static sitePrice(item: IArticle | ICustomerArticle | ICustomerOptionGroupOption): number {
        const useCase = this.getCurrentOrderServiceOrderApplication();
        const site = useCase === undefined ? ConsumptionSite.OnSite : useCase.consumptionSite;

        let price;
        if (site === ConsumptionSite.OffSite && item.oohPrice !== undefined) price = item.oohPrice;
        else price = item.price;

        return price === undefined ? 0 : price;
    }

    public static isSingleService(): boolean {
        return this.getAvailableServices().length === 1;
    }

    public static isPayService(): boolean {
        return this.getCurrentPayService() != null;
    }

    public static isOrderOrOfferService(): boolean {
        return this.getCurrentOrderOrOfferService() != null;
    }

    public static getCovidServiceConfigValue<T>(key: string): T | undefined {
        const covidService = this.getAvailableServices().find((c) => c.serviceType == ServiceType.Covid);
        if (covidService?.config === undefined || covidService.config[key] === undefined) {
            return undefined;
        }

        return covidService.config[key];
    }

    public static getCovidServiceConfig(): object | undefined {
        const selectedServiceId = TastierClient.context.selectedServiceId;
        const s = this.getAvailableServices().find(
            (c) => c.serviceType == ServiceType.Covid && (selectedServiceId == null || c.id == selectedServiceId)
        );
        if (s === undefined) return undefined;

        return s.config;
    }

    private static getAvailableServices(): IService[] {
        return TastierClient.context?.currentAllocation?.services ?? [];
    }

    public static getAvailableOrderOrOfferServices(): IService[] {
        return this.getAvailableServices().filter(
            (x) => x.serviceType == ServiceType.Order || x.serviceType == ServiceType.Offer
        );
    }

    public static getAvailablePaymentServices(): IService[] {
        return this.getAvailableServices().filter((x) => x.serviceType == ServiceType.Pay);
    }
}
